import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { TrendingUp, CheckCircle2, ArrowUpRight, Wallet, Users, DollarSign, Percent, Clock } from "@/components/icons";
import { PaymentModal } from "../../components/AppointmentsTable";
import { TableSkeleton } from "../../components/Skeletons";
import AppleKpi from "../../components/AppleKpi";
import { todayLocal } from "../../lib/dateLocal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const DAY_MS = 24 * 60 * 60 * 1000;
function weekStartTue(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const daysSinceTue = (day - 2 + 7) % 7;
  x.setDate(x.getDate() - daysSinceTue);
  return x;
}
const fmtShort = (d) =>
  d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });

const Th = ({ children }) => (
  <th className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold text-left">
    {children}
  </th>
);

export default function Finanzas() {
  const [d, setD] = useState(null);
  const [charging, setCharging] = useState(null);
  const { pharmacyEnabled } = useSettings();
  const navigate = useNavigate();

  const refresh = () => api.get("/doctor/finance").then((r) => setD(r.data));

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, []);

  const weeks = useMemo(() => {
    if (!d || !d.pharmacy_sales) return [];
    const buckets = {};
    for (const s of d.pharmacy_sales) {
      const start = weekStartTue(s.created_at);
      const key = start.toISOString().slice(0, 10);
      if (!buckets[key]) {
        buckets[key] = {
          key,
          start,
          end: new Date(start.getTime() + 6 * DAY_MS),
          payout: new Date(start.getTime() + 7 * DAY_MS),
          sales: [],
          amount: 0,
          pending: 0,
          paid: 0,
        };
      }
      const b = buckets[key];
      const amt = s.doctor_amount || 0;
      b.sales.push(s);
      b.amount += amt;
      if (s.doctor_paid) b.paid += amt;
      else b.pending += amt;
    }
    return Object.values(buckets).sort((a, b) => b.start - a.start);
  }, [d]);

  if (!d)
    return (
      <div className="flex-1 min-h-0 space-y-3" data-testid="finance-loading">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-[22px] bg-[#F5F5F7] p-5 min-h-[180px]">
              <span className="apple-skeleton w-24 h-4 rounded" />
              <span className="apple-skeleton w-32 h-3 rounded mt-2 inline-block" />
            </div>
          ))}
        </div>
        <TableSkeleton rows={5} />
      </div>
    );

  // ── Filtro "sólo hoy" ──────────────────────────────────────────────
  // El KPI "Cobrado" y el bloque "Pagos cobrados" se limpian al pasar el
  // día — al día siguiente aparecen vacíos, sólo muestran los cobros del
  // día en curso.
  const todayKey = todayLocal();
  const todayPayments = d.payments.filter(
    (p) => (p.paid_at || p.created_at || "").slice(0, 10) === todayKey,
  );
  const todayRevenue = todayPayments
    .filter((p) => (p.status || "paid") === "paid")
    .reduce((s, p) => s + Number(p.amount || 0), 0);

  const paidPct = d.total_patient_revenue + d.pharmacy_commission_total
    ? Math.round(
        (d.total_patient_revenue /
          (d.total_patient_revenue + d.pharmacy_commission_total)) *
          100
      )
    : 100;

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="finanzas-page">
      {/* ── KPI ROW ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MoneyGauge
          label="Cobrado hoy"
          sub="A pacientes · del día"
          value={`$${todayRevenue.toFixed(2)}`}
          pct={paidPct}
          pctLabel="de ingresos"
          testid="stat-patient-revenue"
        />
        {pharmacyEnabled ? (
          <MoneySpectrum
            label="Comisión farmacia"
            sub={`Volumen $${d.pharmacy_total_volume.toFixed(0)}`}
            value={`$${d.pharmacy_commission_total.toFixed(2)}`}
            total={d.pharmacy_commission_total + d.pharmacy_commission_pending}
            current={d.pharmacy_commission_total}
            testid="stat-pharmacy-commission"
          />
        ) : (
          <ProgressCard
            label="Método más usado"
            sub="—"
            value="—"
            pct={0}
            testid="stat-methods"
          />
        )}
        {pharmacyEnabled ? (
          <ProgressCard
            label="Pendiente"
            sub={`Pagado $${d.pharmacy_commission_paid.toFixed(0)}`}
            value={`$${d.pharmacy_commission_pending.toFixed(2)}`}
            pct={
              d.pharmacy_commission_total + d.pharmacy_commission_pending
                ? Math.round(
                    (d.pharmacy_commission_pending /
                      (d.pharmacy_commission_total +
                        d.pharmacy_commission_pending)) *
                      100
                  )
                : 0
            }
            color="#F5A524"
            testid="stat-pharmacy-pending"
          />
        ) : null}
        <ProgressCard
          label="Citas sin cobrar"
          sub="Pasadas sin pago"
          value={d.pending_appointments_count}
          pct={
            d.pending_appointments_count && d.payments.length
              ? Math.round(
                  (d.pending_appointments_count /
                    (d.pending_appointments_count + d.payments.length)) *
                    100
                )
              : d.pending_appointments_count
              ? 100
              : 0
          }
          color="#EF4444"
          testid="stat-pending-appts"
        />
      </div>

      {/* ── MAIN SCROLLABLE ─────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar space-y-3">
        {pharmacyEnabled && d.monthly.length > 0 && (
          <div
            className="bg-white rounded-xl border border-black/[0.05] p-4"
            data-testid="finance-monthly"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[13px] font-semibold text-black flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                  Ingresos mensuales
                </div>
                <div className="text-[11px] text-black/45 mt-0.5">
                  Pacientes vs Farmacia
                </div>
              </div>
              <span className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-[#0071E3]" strokeWidth={1.75} weight="regular" />
              </span>
            </div>
            <div className="h-48">
              <ResponsiveContainer>
                <BarChart data={d.monthly} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                  <XAxis dataKey="month" stroke="#00000060" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#00000060" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 10,
                      fontSize: 12,
                    }}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  />
                  <Bar dataKey="patients" name="Pacientes" fill="#111" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pharmacy" name="Farmacia" fill="#8FE388" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {pharmacyEnabled && (
          <FinanceCard
            title="Ventas por farmacias"
            icon={Wallet}
            subtitle={
              weeks.length === 0
                ? "Sin ventas todavía"
                : "Liquidaciones semanales (mar → lun, pago el martes siguiente)"
            }
            testid="finance-pharmacy-block"
          >
            {weeks.length === 0 ? (
              <EmptyRow text="Sin ventas de farmacia todavía." />
            ) : (
              <table className="w-full text-sm" data-testid="finance-pharmacy-table">
                <thead>
                  <tr>
                    <Th>Semana</Th>
                    <Th>Pago</Th>
                    <Th># Ventas</Th>
                    <Th>Volumen</Th>
                    <Th>Tu parte</Th>
                    <Th>Estado</Th>
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((w) => {
                    const volume = w.sales.reduce(
                      (a, s) => a + (s.total_amount || 0),
                      0,
                    );
                    return (
                      <tr
                        key={w.key}
                        data-testid={`finance-pharmacy-week-${w.key}`}
                        onClick={() =>
                          navigate(`/doctor/finanzas/semana/${w.key}`)
                        }
                        className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition cursor-pointer"
                      >
                        <td className="py-3 pr-3 whitespace-nowrap">
                          <div className="text-[13px] font-semibold text-black">
                            {fmtShort(w.start)} → {fmtShort(w.end)}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-[13px] text-black/70 whitespace-nowrap">
                          {fmtShort(w.payout)}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-black/70">
                          {w.sales.length}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-black/70 whitespace-nowrap">
                          ${volume.toFixed(2)}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="text-[14px] font-semibold text-black">
                            ${w.amount.toFixed(2)}
                          </div>
                          {w.pending > 0 && (
                            <div className="text-[10px] text-amber-700 font-semibold">
                              pendiente ${w.pending.toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pl-3">
                          {w.pending <= 0 ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-semibold">
                              <CheckCircle2 className="w-3 h-3" strokeWidth={1.75} weight="regular" /> Cobrada
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 font-semibold">
                              Pendiente
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </FinanceCard>
        )}

        {d.pending_appointments.length > 0 && (
          <FinanceCard
            title="Citas pendientes de cobro"
            icon={Users}
            subtitle={`${d.pending_appointments.length} pendiente${
              d.pending_appointments.length === 1 ? "" : "s"
            }`}
            testid="finance-pending-block"
          >
            <table className="w-full text-sm" data-testid="finance-pending-table">
              <thead>
                <tr>
                  <Th>Paciente</Th>
                  <Th>Fecha y hora</Th>
                  <Th>Motivo</Th>
                  <Th>Estado</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {d.pending_appointments.slice(0, 10).map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`finance-pending-row-${a.id}`}
                    className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-[#F2F3F5] text-black text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                          {initials(a.patient_name)}
                        </div>
                        <span className="font-semibold text-[14px] truncate max-w-[260px]">
                          {a.patient_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/70 whitespace-nowrap">
                      {new Date(a.date).toLocaleString("es-ES", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/70 truncate max-w-[280px]">
                      {a.reason || "Consulta"}
                    </td>
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 font-semibold">
                        Sin cobrar
                      </span>
                    </td>
                    <td className="py-3 pl-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setCharging(a)}
                        data-testid={`finance-cobrar-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-black text-white hover:bg-black/85 transition"
                      >
                        <DollarSign className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                        Cobrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </FinanceCard>
        )}

        <FinanceCard
          title="Pagos cobrados hoy"
          icon={Wallet}
          subtitle={
            todayPayments.length === 0
              ? "Sin cobros registrados hoy"
              : `${todayPayments.length} cobro${todayPayments.length === 1 ? "" : "s"} registrado${todayPayments.length === 1 ? "" : "s"}`
          }
          testid="finance-payments-block"
        >
          {todayPayments.length === 0 ? (
            <EmptyRow text="Sin cobros registrados hoy." />
          ) : (
            <table className="w-full text-sm" data-testid="finance-payments-table">
              <thead>
                <tr>
                  <Th>Paciente</Th>
                  <Th>Fecha</Th>
                  <Th>Método</Th>
                  <Th>Concepto</Th>
                  <Th>Monto</Th>
                </tr>
              </thead>
              <tbody>
                {todayPayments.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`finance-payment-row-${p.id}`}
                    className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-[#F2F3F5] text-black text-[12px] font-bold flex items-center justify-center flex-shrink-0">
                          {initials(p.patient_name)}
                        </div>
                        <span className="font-semibold text-[14px] truncate max-w-[260px]">
                          {p.patient_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/70 whitespace-nowrap">
                      {new Date(p.created_at).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/70 capitalize">
                      {p.method}
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/70 truncate max-w-[260px]">
                      {p.concept || "Pago"}
                    </td>
                    <td className="py-3 pl-3 text-[14px] font-semibold text-emerald-700 whitespace-nowrap">
                      +${p.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </FinanceCard>
      </div>

      {charging && (
        <PaymentModal
          appt={charging}
          patient={{ full_name: charging.patient_name }}
          onClose={() => setCharging(null)}
          onSaved={() => {
            setCharging(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function FinanceCard({ title, subtitle, testid, icon: Icon, children }) {
  return (
    <div
      className="bg-white rounded-xl border border-black/[0.05] p-4"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-black flex items-center gap-1.5">
            {Icon && <Icon className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />}
            {title}
          </div>
          {subtitle && (
            <div className="text-[11px] text-black/45 mt-0.5">{subtitle}</div>
          )}
        </div>
        <span className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0">
          <ArrowUpRight className="w-6 h-6 text-[#0071E3]" strokeWidth={1.75} weight="regular" />
        </span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function EmptyRow({ text }) {
  return (
    <div className="text-[13px] text-black/50 py-6 text-center">{text}</div>
  );
}

function MoneyGauge({ label, sub, value, pct, pctLabel, testid, icon: Icon = Wallet }) {
  return (
    <AppleKpi
      testid={testid}
      icon={Icon}
      title={label}
      description={sub}
      label={pctLabel || "Monto"}
      value={value}
      footer={typeof pct === "number" && pctLabel ? `${pct}% ${pctLabel}` : null}
    />
  );
}

function MoneySpectrum({ label, sub, value, total, current, testid, icon: Icon = Percent }) {
  const pct = total ? Math.round(((current || 0) / total) * 100) : 0;
  return (
    <AppleKpi
      testid={testid}
      icon={Icon}
      title={label}
      description={sub}
      label="Comisión"
      value={value}
      footer={total ? `${pct}% cobrado del total generado` : null}
    />
  );
}

function ProgressCard({ label, sub, value, pct, testid, color = "#111", icon: Icon = Clock }) {
  return (
    <AppleKpi
      testid={testid}
      icon={Icon}
      title={label}
      description={sub}
      label="Actual"
      value={value}
      footer={typeof pct === "number" && pct > 0 ? `${pct}% del total` : null}
    />
  );
}
